// The job runner and the MCP server used to be two separate processes
// (4790 + 4791) — now merged into one (see automation/mcp-server/server.mjs)
// so Carrier Access and the agent share the exact same automation engine.
const AUTOMATION_BASE = process.env.REACT_APP_AUTOMATION_URL || 'http://localhost:4791';

const isLocalServer = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/.test(AUTOMATION_BASE);
const TOKEN_KEY = 'carrierAccessToken';
const EMAIL_KEY = 'carrierAccessEmail';
const stored = (key: string): string => { try { return window.localStorage.getItem(key) ?? ''; } catch { return ''; } };

/**
 * Calls the carrier server. A server on this computer needs nothing. A hosted one (staging) wants the shared sign-in token and the
 * person's email (its allow-list), sent as headers; they are asked for once, kept in this browser, and asked for again if refused.
 */
async function automationFetch(url: string, init: RequestInit = {}, asked = false): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!isLocalServer) {
    if (stored(TOKEN_KEY)) headers.set('Authorization', `Bearer ${stored(TOKEN_KEY)}`);
    if (stored(EMAIL_KEY)) headers.set('X-User-Email', stored(EMAIL_KEY));
  }
  const response = await fetch(url, { ...init, headers });
  if (!isLocalServer && !asked && (response.status === 401 || response.status === 403)) {
    const token = window.prompt('Carrier Access sign-in token (ask the team lead):', stored(TOKEN_KEY));
    const email = token ? window.prompt('Your Momentum email:', stored(EMAIL_KEY)) : null;
    if (token && email) {
      try { window.localStorage.setItem(TOKEN_KEY, token.trim()); window.localStorage.setItem(EMAIL_KEY, email.trim().toLowerCase()); } catch { /* not kept */ }
      return automationFetch(url, init, true);
    }
  }
  return response;
}

export interface CarrierPolicy {
  policyNumber: string;
  name: string;
  [key: string]: unknown;
}

export interface JobStatus {
  // 'needs_manual_login' (carrier-browser mode): the run works in a browser a person logged into by hand, and that
  // browser is closed or logged out — only that person logging in again fixes it.
  // 'needs_2fa_code': the run is paused on the carrier's 2FA screen, waiting for submitTwoFactorCode.
  // 'needs_agent_action': the run is paused on a person (a one-time code, the final click of a policy change) and carries
  // on by itself once they act on the hand-off card.
  status: 'running' | 'needs_attention' | 'needs_manual_login' | 'needs_2fa_code' | 'needs_agent_action' | 'completed' | 'failed';
  runId?: string;
  /** A carrier login's result (LOGIN_JSON). */
  loginJson?: { carrier: string; label: string; loggedIn: boolean; how: string; note?: string } | null;
  /** The waiting screen's card, when a run is paused on a person. */
  handoffCard?: unknown;
  log: string;
  resultPath: string | null;
  policiesJson: CarrierPolicy[] | null;
  /** A policy's document history (DOCUMENTS_JSON) — Progressive and Liberty Mutual. */
  documentsJson?: PolicyDocumentRow[] | null;
  /** A quote script's structured result (QUOTE_JSON) — currently Progressive Home. */
  quoteJson?: HomeQuoteResult | null;
  /** A read-only lookup's rows (REPORT_JSON) — dashboards, reports, policy detail. */
  reportJson?: { rows: Record<string, unknown>[]; note?: string } | null;
  /** A quote script's RESULT_JSON — Openly: status ready_to_bind / not_eligible, policy number, premium, RCE. */
  resultJson?: { status: string; policyNumber?: string; termPremium?: string | null; rce?: string | null; reason?: string } | null;
  attentionMessage: string | null;
  resumed: boolean;
  label: string;
  /** Set once the job's process has exited, whatever status it ended on. Older servers don't send it. */
  finished?: boolean;
}

export interface PolicyCache {
  updatedAt: string;
  policies: CarrierPolicy[];
  lastChange: { added: CarrierPolicy[]; removed: CarrierPolicy[] };
}

export function signedInEmail(): string | undefined {
  try {
    return localStorage.getItem('userEmail') || undefined;
  } catch {
    return undefined;
  }
}

const NOTIFY_EMAIL_KEY = 'carrierNotifyEmail';

/** Where this person asked Carrier Access to email documents — falls back to their sign-in email. */
function notifyEmailForRuns(): string | undefined {
  try {
    return localStorage.getItem(NOTIFY_EMAIL_KEY) || signedInEmail();
  } catch {
    return signedInEmail();
  }
}

export async function getNotifyEmail(user: string): Promise<string | null> {
  const response = await automationFetch(`${AUTOMATION_BASE}/notify-email?user=${encodeURIComponent(user)}`);
  if (!response.ok) throw new Error(`Failed to load notify email (${response.status})`);
  const { email } = await response.json();
  try {
    if (email) localStorage.setItem(NOTIFY_EMAIL_KEY, email);
  } catch {
    // localStorage unavailable — the server copy still applies.
  }
  return email;
}

export async function saveNotifyEmail(user: string, email: string): Promise<string> {
  const response = await automationFetch(`${AUTOMATION_BASE}/notify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user, email }),
  });
  if (!response.ok) throw new Error(await response.text());
  const saved = (await response.json()).email as string;
  try {
    localStorage.setItem(NOTIFY_EMAIL_KEY, saved);
  } catch {
    // localStorage unavailable — the server copy still applies.
  }
  return saved;
}

export async function runJob(jobKey: string, extraArgs: string[] = [], agencyId?: string): Promise<string> {
  const response = await automationFetch(`${AUTOMATION_BASE}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // interactive2fa: someone is watching this run, so a carrier that supports
    // it can pause on 2FA and ask for the code here instead of stopping.
    body: JSON.stringify({ jobKey, extraArgs, agencyId, notifyEmail: notifyEmailForRuns(), interactive2fa: true }),
  });
  if (!response.ok) throw new Error(`Failed to start ${jobKey} (${response.status})`);
  const { runId } = await response.json();
  return runId;
}

export async function getStatus(runId: string): Promise<JobStatus> {
  const response = await automationFetch(`${AUTOMATION_BASE}/status/${runId}`);
  if (!response.ok) throw new Error(`Unknown run ${runId}`);
  return response.json();
}

/**
 * Starts a job and polls until it lands on a terminal state. `needs_attention`
 * (a 2FA prompt, a bot-detection wall) is reported to onUpdate like any other
 * status rather than treated as terminal — the underlying script may clear it
 * and resume on its own, same behavior the sandbox UI relied on.
 */
export async function runAndWait(
  jobKey: string,
  extraArgs: string[] = [],
  onUpdate?: (status: JobStatus, runId: string) => void,
  agencyId?: string,
): Promise<JobStatus> {
  const runId = await runJob(jobKey, extraArgs, agencyId);
  for (;;) {
    const status = await getStatus(runId);
    onUpdate?.(status, runId);
    // `finished` also ends runs that exited while still marked needs_attention
    // (e.g. stopped on 2FA), which would otherwise be polled forever.
    if (status.finished || status.status === 'completed' || status.status === 'failed') return status;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
}

/** Hands the 2FA code the person received to a run paused on needs_2fa_code. */
export async function submitTwoFactorCode(runId: string, code: string, agencyId?: string): Promise<void> {
  const response = await automationFetch(`${AUTOMATION_BASE}/run/${encodeURIComponent(runId)}/2fa-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, agencyId }),
  });
  if (!response.ok) throw new Error((await response.text().catch(() => '')) || `Failed to submit code (${response.status})`);
}

export function resolveDownloadUrl(resultPath: string): string {
  return `${AUTOMATION_BASE}${resultPath}`;
}

export async function getCachedPolicies(): Promise<Record<string, PolicyCache | null>> {
  const response = await automationFetch(`${AUTOMATION_BASE}/policies`);
  if (!response.ok) throw new Error(`Failed to load policies (${response.status})`);
  return response.json();
}

export interface CredentialStatus {
  configured: boolean;
  username?: string;
  /**
   * Which agent (Digital Workforce / Command Center) this agency considers
   * this carrier connection wired up for — self-reported locally (see
   * /agency-credentials/:agencyId/:carrierId/agent), not verified against
   * anything real. Only ever set for agency-saved connections, never for
   * the .env.local fallback. null/undefined = connected to the carrier but
   * not attached to any agent yet.
   */
  attachedAgent?: string | null;
  /** True when connected only through the hand-logged-in carrier browser (CARRIER_BROWSER_URL), with no stored login. */
  viaBrowser?: boolean;
  /** A login is stored on this computer for "log in from the chat". Absent when the carrier has no login recipe. */
  loginStored?: boolean;
  loginUsername?: string;
  /** When this carrier was logged in today (kept by the server across refreshes and restarts); absent when not logged in. */
  loggedInAt?: string;
  /** Logged in right now, even when the time it happened is not known (a carrier whose login lives in its open tab). */
  loggedIn?: boolean;
  /** When the server stops keeping this carrier's session warm, if it is. */
  loggedInUntil?: string;
}

/**
 * Whether real credentials exist in .env.local on this machine for each
 * carrier — never includes the password, only whether one is set and the
 * real username to display. Lets Carrier Access show "Connected" based on
 * a real, verifiable fact instead of a manually-flipped local toggle.
 */
export async function getCredentialsStatus(): Promise<Record<string, CredentialStatus>> {
  const response = await automationFetch(`${AUTOMATION_BASE}/credentials-status`);
  if (!response.ok) throw new Error(`Failed to load credentials status (${response.status})`);
  return response.json();
}

/**
 * Same shape as getCredentialsStatus, but for one agency's own saved portal
 * logins (see /agency-credentials in server.mjs) instead of the single
 * .env.local on this machine — this is what makes "Connected" mean
 * something real for an agency other than yours.
 */
export async function getAgencyCredentialsStatus(agencyId: string): Promise<Record<string, CredentialStatus>> {
  const response = await automationFetch(`${AUTOMATION_BASE}/agency-credentials/${encodeURIComponent(agencyId)}`);
  if (!response.ok) throw new Error(`Failed to load agency credentials status (${response.status})`);
  return response.json();
}

export async function saveAgencyCredentials(agencyId: string, carrierId: string, username: string, password: string): Promise<void> {
  const response = await automationFetch(`${AUTOMATION_BASE}/agency-credentials/${encodeURIComponent(agencyId)}/${encodeURIComponent(carrierId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) throw new Error(`Failed to save credentials (${response.status})`);
}

export async function removeAgencyCredentials(agencyId: string, carrierId: string): Promise<void> {
  const response = await automationFetch(`${AUTOMATION_BASE}/agency-credentials/${encodeURIComponent(agencyId)}/${encodeURIComponent(carrierId)}/remove`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error(`Failed to remove credentials (${response.status})`);
}

/** Pass an empty string to detach. */
export async function setAttachedAgent(agencyId: string, carrierId: string, attachedAgent: string): Promise<void> {
  const response = await automationFetch(`${AUTOMATION_BASE}/agency-credentials/${encodeURIComponent(agencyId)}/${encodeURIComponent(carrierId)}/agent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attachedAgent }),
  });
  if (!response.ok) throw new Error(`Failed to update attached agent (${response.status})`);
}

export interface RunLogEntry {
  runId: string;
  label: string;
  status: 'completed' | 'failed' | 'needs_attention' | 'running';
  agencyId: string | null;
  resultPath: string | null;
  finishedAt: string;
  logExcerpt: string;
}

/** Newest-first history of finished runs for one carrier (see appendRunLog in server.mjs). */
export async function getRunLogs(carrierId: string): Promise<RunLogEntry[]> {
  const response = await automationFetch(`${AUTOMATION_BASE}/run-logs/${encodeURIComponent(carrierId)}`);
  if (!response.ok) throw new Error(`Failed to load run logs (${response.status})`);
  return response.json();
}

export interface CarrierSettings {
  placeholdersEnabled: boolean;
  /** Per carrier-browser carrier: run the work hidden once the session is confirmed (default on). */
  hiddenAfterLogin?: Record<string, boolean>;
  /** Per carrier: the agency's default answers, one "question: answer" per line (automation/lib/answerRules.ts). */
  answerRules?: Record<string, string>;
  /** Per carrier-browser carrier: also close the visible window once the session is confirmed and the hidden copy is verified (default on). */
}

/**
 * Global "Use placeholder info" toggle for quote jobs — when on, a quote
 * missing an underwriting/demographic field (gender, marital status, etc.)
 * gets a generic placeholder instead of blocking. Requested as a single
 * flip-off switch for going live with real customer quotes — see
 * withPlaceholderDefaults() in automation/lib/validateQuoteInput.ts.
 */
export async function getCarrierSettings(): Promise<CarrierSettings> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-settings`);
  if (!response.ok) throw new Error(`Failed to load carrier settings (${response.status})`);
  return response.json();
}

export async function setPlaceholdersEnabled(placeholdersEnabled: boolean): Promise<CarrierSettings> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ placeholdersEnabled }),
  });
  if (!response.ok) throw new Error(`Failed to update carrier settings (${response.status})`);
  return response.json();
}

// ─── Added from Kieran's carrier-browser package (2026-10-12). Additive only. ───

export interface CarrierSession {
  carrier: string;
  url: string;
  pageIsOpen: boolean;
  policyNumber: string | null;
  verifiedAt: string;
  /** When the server stops keeping this session alive (it touches it every few minutes so the carrier doesn't end it as idle). */
  keepAliveUntil?: string;
  /** True when the visible carrier browser window was closed after login (the work runs hidden with the saved session). */
  windowClosed?: boolean;
  /** True when the session was confirmed from the saved copy alone (no visible window needed). */
  workRunsHidden?: boolean;
}

export interface CarrierSessionCheck {
  ok: boolean;
  message: string;
  session?: CarrierSession;
}

/** The logged-in carrier page last shared with the automation server, if any — see CarrierSessionBar. */
export async function getCarrierSession(carrierId: string): Promise<CarrierSession | null> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-session?carrier=${encodeURIComponent(carrierId)}`);
  if (!response.ok) throw new Error(`Failed to load the carrier session (${response.status})`);
  return response.json();
}

/**
 * Hands the server a URL copied from a logged-in carrier page. The server
 * confirms the carrier browser really is logged in before saving it — the
 * URL itself carries no login.
 */
export async function shareCarrierSession(url: string, carrierId: string): Promise<CarrierSessionCheck> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, carrier: carrierId }),
  });
  if (!response.ok) throw new Error(`Failed to check that address (${response.status})`);
  return response.json();
}

export async function clearCarrierSession(carrierId: string): Promise<void> {
  await automationFetch(`${AUTOMATION_BASE}/carrier-session/clear`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ carrier: carrierId }),
  });
}

export interface PolicyDocumentRow {
  date: string;
  type: string;
  delivery: string;
}

export interface HomeQuoteResult {
  quoteNumber: string | null;
  totalPremium: string | null;
  dwellingCoverage: string | null;
  replacementCostEstimate: string | null;
  packages: Record<string, string>;
  status: string;
}

/** Saves one carrier's default answers ("Employment status: Retired", one per line); empty text removes them. */
export async function setAnswerRules(carrierId: string, text: string): Promise<CarrierSettings> {
  const response = await fetch(`${AUTOMATION_BASE}/carrier-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answerRules: { [carrierId]: text } }),
  });
  if (!response.ok) throw new Error((await response.text().catch(() => '')) || `Failed to save the rules (${response.status})`);
  return response.json();
}

/** "Run hidden after login" for one carrier-browser carrier — see lib/carrierHeadless.ts on the automation server. */
export async function setHiddenAfterLogin(carrierId: string, hidden: boolean): Promise<CarrierSettings> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hiddenAfterLogin: { [carrierId]: hidden } }),
  });
  if (!response.ok) throw new Error(`Failed to update the setting (${response.status})`);
  return response.json();
}

/** The signed-in person's first name, for "<name> is logged in" — falls back to their user name, then the start of their email. */
export function signedInFirstName(): string {
  try {
    const first = (localStorage.getItem('firstName') || '').trim();
    if (first) return first;
    const user = (localStorage.getItem('userName') || '').trim();
    if (user) return user.split(/[\s@]/)[0];
    const email = (localStorage.getItem('userEmail') || '').trim();
    if (email) return email.split('@')[0];
  } catch {
    // storage unavailable
  }
  return 'You';
}


/** The signed hand-off link for a run that is waiting on a person (Carrier Access on this computer shows it as a card). */
export async function getHandoffLink(runId: string): Promise<string | null> {
  const response = await automationFetch(`${AUTOMATION_BASE}/handoff-link/${runId}`);
  if (!response.ok) return null;
  return (await response.json()).url ?? null;
}

/** Stores (or, with null, removes) a carrier's login on this computer. The password is never returned or logged. */
export async function storeCarrierLogin(carrier: string, login: { username: string; password: string } | null): Promise<{ ok: boolean; message?: string }> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(login ? { carrier, ...login } : { carrier, remove: true }),
  });
  return response.json();
}

/** Stops a run that is working or waiting on a person, so whatever is queued behind it carries on. */
export async function cancelRun(runId: string): Promise<void> {
  await automationFetch(`${AUTOMATION_BASE}/run/${encodeURIComponent(runId)}/cancel`, { method: 'POST' }).catch(() => {});
}

/** Everything still running or waiting on a person, with the carrier each belongs to. */
export async function getActiveRuns(): Promise<{ runId: string; label: string; carrier: string | null; status: string }[]> {
  const response = await automationFetch(`${AUTOMATION_BASE}/active-runs`);
  if (!response.ok) return [];
  return response.json();
}

/** The X on a carrier's tile: stops every login or job still running for that carrier. */
export async function cancelCarrierRuns(carrierId: string): Promise<void> {
  await automationFetch(`${AUTOMATION_BASE}/carrier/${encodeURIComponent(carrierId)}/cancel`, { method: 'POST' }).catch(() => {});
}

// ----- Carrier locks (National General, Travelers): unlock with a code, lock again -----
export interface CarrierLocks {
  locked: string[];
  unlockedUntil: Record<string, number>;
  codeSet: boolean;
}

export async function getCarrierLocks(): Promise<CarrierLocks> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-lock`);
  if (!response.ok) throw new Error(`Failed to load carrier locks (${response.status})`);
  return response.json();
}

export async function unlockCarrier(carrier: string, code: string): Promise<void> {
  const response = await automationFetch(`${AUTOMATION_BASE}/carrier-lock/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ carrier, code }),
  });
  if (!response.ok) throw new Error(await response.text());
}

export async function relockCarrier(carrier: string): Promise<void> {
  await automationFetch(`${AUTOMATION_BASE}/carrier-lock/lock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ carrier }),
  });
}
